import { use, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { defaultVoiceValuesNoSoprano, OrgType, TutorialPages, Voice } from '../../../../common/src/model.ts';
import { Pages } from '../../../../common/src/pages.ts';
import { getSongSlug } from '../../../../common/src/paths.ts';
import {
  localStorageGet,
  LocalStorageKey,
  localStorageSet,
  SongIntroduction,
} from '../../data/client_local_storage.ts';
import { getSongListFromHymns } from '../../data/hymnals.ts';
import { CurrentOrgMockProvider } from '../../data/test/use_current_org.mock.tsx';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { CurrentSongContext } from '../../player/current_song.tsx';
import { mixerButtonId } from '../../player/mixer.tsx';
import { playButtonId } from '../../player/play_button.tsx';
import { SequencerContext } from '../../player/sequencer.tsx';
import { kebabCloseButtonId } from '../../util/kebab.tsx';
import { getUrlForPage } from '../../util/path.ts';
import { pdfMinimizeButtonId, songKebabButtonId } from '../../util/pdf_viewer.tsx';
import { useCurrentDate } from '../../util/use_current_date.tsx';
import { getFooterTabId } from '../footer.tsx';
import { OrgPage } from '../org_page.tsx';
import {
  getSongIntroductionOptionId,
  showPdfOnPlaySettingId,
  songIntroductionSettingId,
  SongOptionsSetting,
} from '../settings/song_options.tsx';
import { getTodaySongElementId } from '../today/singing_plan_tile.tsx';
import { TodayPage } from '../today/today_page.tsx';
import { Spotlight, SpotlightPortalContext, spotlightTextBorderRadius } from './components/spotlight.tsx';
import { useCompleteTutorial } from './use_complete_tutorial.tsx';

export const title = 'How to Lead Family Worship';

const firstSongSlug = getSongSlug({ songNumber: '510', hymnalSlug: 'tr2022' }); // Holy, Holy, Holy

interface StepClickContext {
  setIsMixerVisible: (visible: boolean) => void;
  openPlayer: (args: { playlist: ReturnType<typeof getSongListFromHymns>; addHistoryEntry?: boolean }) => void;
  setTutorialPlayingSong: (playing: boolean) => void;
}

export interface TutorialStepConfig {
  name: string;
  text?: string;
  messageBottom?: string;
  targetElementId?: string;
  isCircularSpotlight?: boolean;
  padding?: number;
  suppressSpotlight?: boolean;
  shouldFadeMessageDialog?: boolean;
  postStepAction?: (ctx: StepClickContext) => { delayNextStep: boolean };
  page: React.ReactNode;
  state: {
    isPlayerMaximized?: boolean;
    isMixerVisible?: boolean;
    areSongSettingsVisible?: boolean;
    expandedKebabSetting?: SongOptionsSetting;
    playing?: boolean;
    tutorialPlayingSong?: boolean;
  };
}

export const familyWorshipStepConfigs: TutorialStepConfig[] = [
  {
    name: 'Introduce church tab',
    text: 'This is the "Church" tab.  All of your church\'s music is found here.',
    messageBottom: '30%',
    page: <OrgPage />,
    state: {},
  },
  {
    name: 'Prompt for today tab',
    text: 'To make things easier, the "Today" tab is the best place to start.',
    messageBottom: '30%',
    page: <OrgPage />,
    state: {},
  },
  {
    name: 'Click on today tab',
    targetElementId: getFooterTabId(Pages.Today),
    isCircularSpotlight: true,
    page: <OrgPage />,
    state: {},
  },
  {
    name: 'Describe today tab',
    text: "We'll give you a few songs here everyday that we know will help you prepare for your church's worship.",
    messageBottom: '30%',
    page: <TutorialTodayPage />,
    state: {},
  },
  {
    name: 'Click on song',
    targetElementId: getTodaySongElementId(firstSongSlug),
    padding: 10,
    postStepAction: ({ setIsMixerVisible, openPlayer }) => {
      setIsMixerVisible(true);
      openPlayer({
        playlist: getSongListFromHymns('Tutorial Playlist', [{ slug: firstSongSlug }]),
        // the tutorial closes the player itself; a history entry here made Back from the
        // tutorials list land on the tutorial again
        addHistoryEntry: false,
      });
      // Wait until the player finishes sliding up before transitioning to the next step.
      return { delayNextStep: true };
    },
    page: <TutorialTodayPage />,
    state: {},
  },
  {
    name: 'Mention melody mode',
    text: '"Melody mode" is the best way to get everyone involved fast',
    messageBottom: '175px',
    page: <TutorialTodayPage />,
    state: { isPlayerMaximized: true, isMixerVisible: true },
  },
  {
    name: 'Prompt for song introduction',
    text: "It's hard to start on the first note; let's add an introduction.",
    messageBottom: '175px',
    page: <TutorialTodayPage />,
    state: { isPlayerMaximized: true, isMixerVisible: true },
  },
  {
    name: 'Open song settings',
    targetElementId: songKebabButtonId,
    isCircularSpotlight: true,
    padding: 15,
    postStepAction: () => {
      document.getElementById(songKebabButtonId)?.click();
      // Wait until the kebab menu finishes sliding up before moving the spotlight.
      return { delayNextStep: true };
    },
    page: <TutorialTodayPage />,
    state: { isPlayerMaximized: true, isMixerVisible: true },
  },
  {
    name: 'Click on song introduction',
    targetElementId: songIntroductionSettingId,
    postStepAction: () => {
      document.getElementById(songIntroductionSettingId)?.click();
      // Wait until the song introduction menu finishes sliding in from the right.
      return { delayNextStep: true };
    },
    page: <TutorialTodayPage />,
    state: { isPlayerMaximized: true, isMixerVisible: true, areSongSettingsVisible: true },
  },
  {
    name: 'Set the song in introduction to 10 seconds',
    targetElementId: getSongIntroductionOptionId(SongIntroduction.LastTenSeconds),
    postStepAction: () => {
      document.getElementById(getSongIntroductionOptionId(SongIntroduction.LastTenSeconds))?.click();
      return { delayNextStep: true };
    },
    page: <TutorialTodayPage />,
    state: {
      isPlayerMaximized: true,
      isMixerVisible: true,
      areSongSettingsVisible: true,
      expandedKebabSetting: SongOptionsSetting.SongIntroduction,
    },
  },
  {
    name: 'Prompt for show PDF on play',
    text: 'Use "Show PDF on Play" so the mixer isn\'t in your way when you start singing.',
    messageBottom: '175px',
    page: <TutorialTodayPage />,
    state: { isPlayerMaximized: true, isMixerVisible: true, areSongSettingsVisible: true },
  },
  {
    name: 'Click show PDF on play',
    targetElementId: showPdfOnPlaySettingId,
    postStepAction: () => {
      document.getElementById(showPdfOnPlaySettingId)?.click();
      setTimeout(() => document.getElementById(kebabCloseButtonId)?.click(), 500);
      return { delayNextStep: true };
    },
    page: <TutorialTodayPage />,
    state: { isPlayerMaximized: true, isMixerVisible: true, areSongSettingsVisible: true },
  },
  {
    name: 'Click play button',
    targetElementId: playButtonId,
    isCircularSpotlight: true,
    padding: 15,
    postStepAction: ({ setTutorialPlayingSong }) => {
      document.getElementById(playButtonId)?.click();
      setTutorialPlayingSong(true);
      return { delayNextStep: false };
    },
    page: <TutorialTodayPage />,
    state: { isPlayerMaximized: true, isMixerVisible: true },
  },
  {
    name: 'Mention first first and landscape',
    text: "Now let's sing the first verse.  To make it more readable, use landscape.",
    messageBottom: '50%',
    suppressSpotlight: true,
    shouldFadeMessageDialog: true,
    page: <TutorialTodayPage />,
    state: { isPlayerMaximized: true, isMixerVisible: true, playing: true, tutorialPlayingSong: true },
  },
  {
    name: 'Click mixer button',
    targetElementId: mixerButtonId,
    isCircularSpotlight: true,
    padding: 15,
    postStepAction: ({ setTutorialPlayingSong }) => {
      document.getElementById(mixerButtonId)?.click();
      setTutorialPlayingSong(true);
      return { delayNextStep: false };
    },
    page: <TutorialTodayPage />,
    state: { isPlayerMaximized: true, isMixerVisible: true },
  },
  {
    name: 'Mention screen sharing',
    text: "If you don't have a hymnal, screen share to your TV.",
    messageBottom: '175px',
    page: <TutorialTodayPage />,
    state: { isPlayerMaximized: true, isMixerVisible: true },
  },
  {
    name: 'Mention Bluetooth speaker',
    text: 'Remember to use a Bluetooth speaker so everyone can hear.',
    messageBottom: '175px',
    page: <TutorialTodayPage />,
    state: { isPlayerMaximized: true, isMixerVisible: true },
  },
];

export function FamilyWorshipTutorial() {
  const [step, setStep] = useState(() => 1);
  const navigate = useNavigate();
  const completeTutorial = useCompleteTutorial();
  const { openPlayer, playing } = use(SequencerContext);
  const [_, setIsMixerVisible] = useLocalStorage(LocalStorageKey.IsMixerVisible);
  const { setTutorialPlayingSong } = use(CurrentSongContext);
  const [previouslyPlaying, setPreviouslyPlaying] = useState(playing);

  useFamilyWorshipTutorialSetupTeardown();

  // Go to next step when playing transitions from true to false.
  // The setState calls are required here: detecting the transition requires comparing the new
  // `playing` value to the previous one, which is tracked as state.
  useEffect(() => {
    /* eslint-disable @eslint-react/set-state-in-effect */
    if (previouslyPlaying && !playing) {
      setStep((step) => step + 1);
      setTutorialPlayingSong(false);
    }
    setPreviouslyPlaying(playing);
    /* eslint-enable @eslint-react/set-state-in-effect */
  }, [playing, previouslyPlaying, setTutorialPlayingSong]);

  const onClick = useCallback(async () => {
    const config = familyWorshipStepConfigs[step - 1];
    const { delayNextStep } = config.postStepAction?.({ setIsMixerVisible, openPlayer, setTutorialPlayingSong }) ?? {
      delayNextStep: false,
    };
    const nextStep = step + 1;
    if (nextStep > familyWorshipStepConfigs.length) {
      await completeTutorial(TutorialPages.FamilyWorship);
      // replace the finished tutorial, so Back from the list returns to where it was started
      // instead of replaying it (GitHub issue #3551)
      await navigate(getUrlForPage(Pages.Tutorials), { replace: true });
      return;
    }
    setTimeout(() => setStep(nextStep), delayNextStep ? 300 : 0);
  }, [completeTutorial, navigate, openPlayer, setIsMixerVisible, setTutorialPlayingSong, step]);

  const config = familyWorshipStepConfigs[step - 1];

  return <FamilyWorshipTutorialStep {...config} onClick={onClick} />;
}

export function useFamilyWorshipTutorialSetupTeardown() {
  const { clearPlaylist } = use(SequencerContext);
  const { setTutorialPlayingSong } = use(CurrentSongContext);

  useEffect(() => {
    // save and restore all user settings affected by the tutorial
    const originalSongIntroduction = localStorageGet(LocalStorageKey.SongIntroduction);
    const originalShowPdfOnPlay = localStorageGet(LocalStorageKey.PdfAutoDisplay);
    const originalVerseCount = localStorageGet(LocalStorageKey.VerseCount);
    const originalAutoPlayNextSong = localStorageGet(LocalStorageKey.AutoPlayNextSong);
    const originalVoiceValues = localStorageGet(LocalStorageKey.VoiceValues);
    localStorageSet(LocalStorageKey.SongIntroduction, undefined);
    localStorageSet(LocalStorageKey.PdfAutoDisplay, false);
    localStorageSet(LocalStorageKey.VerseCount, 1);
    localStorageSet(LocalStorageKey.AutoPlayNextSong, false);
    localStorageSet(LocalStorageKey.VoiceValues, { ...defaultVoiceValuesNoSoprano, [Voice.Soprano]: 1 });
    // close kebab menu, etc
    document.getElementById(pdfMinimizeButtonId)?.click();
    clearPlaylist();
    return () => {
      localStorageSet(LocalStorageKey.SongIntroduction, originalSongIntroduction);
      localStorageSet(LocalStorageKey.PdfAutoDisplay, originalShowPdfOnPlay);
      localStorageSet(LocalStorageKey.VerseCount, originalVerseCount);
      localStorageSet(LocalStorageKey.AutoPlayNextSong, originalAutoPlayNextSong);
      localStorageSet(LocalStorageKey.VoiceValues, originalVoiceValues);
      document.getElementById(pdfMinimizeButtonId)?.click();
      clearPlaylist();
      setTutorialPlayingSong(false);
    };
  }, [clearPlaylist, setTutorialPlayingSong]);
}

export function FamilyWorshipTutorialStep({
  text,
  onClick,
  page,
  messageBottom,
  targetElementId,
  isCircularSpotlight,
  padding,
  suppressSpotlight,
  shouldFadeMessageDialog,
}: Partial<TutorialStepConfig> & { onClick: () => void }) {
  const portalContainer = use(SpotlightPortalContext);
  const messageRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [opacity, setOpacity] = useState(() => 0);

  const findTargetElement = useCallback(() => {
    if (!targetElementId) return null;
    const container = wrapperRef.current;
    // Scope to this step's subtree first so that when multiple steps are
    // rendered simultaneously (e.g. in Storybook), we find the element
    // belonging to *this* step rather than an earlier one.  Fall back to a
    // document-wide lookup for elements that live outside the tutorial
    // wrapper (e.g. the real player rendered by root_page).
    return (
      container?.querySelector<HTMLElement>(`#${CSS.escape(targetElementId)}`) ??
      document.getElementById(targetElementId)
    );
  }, [targetElementId]);

  // showMessageIfText is called synchronously from the useEffect below. The setters are required
  // here — setTargetElement reads a ref that only exists after commit, and setOpacity drives the
  // fade-in/out animation with setTimeout-based timing that can't be done during render. The rule
  // also traces into the setTimeout callbacks themselves (which run async and are safe), so the
  // block covers those too.
  const showMessageIfText = useCallback(() => {
    /* eslint-disable @eslint-react/set-state-in-effect */
    if (text) {
      setTargetElement(messageRef.current);
      if (shouldFadeMessageDialog) {
        setOpacity(1);
        return setTimeout(() => setOpacity(0), 6000);
      } else {
        return setTimeout(() => setOpacity(1), 750);
      }
    } else {
      if (targetElementId) {
        setTargetElement(findTargetElement());
      } else {
        console.error('Spotlighted message must have either text or targetElementId');
      }
      setOpacity(0);
    }
    /* eslint-enable @eslint-react/set-state-in-effect */
  }, [findTargetElement, shouldFadeMessageDialog, targetElementId, text]);

  useEffect(() => {
    const timer = showMessageIfText();
    return timer !== undefined ? () => clearTimeout(timer) : undefined;
  }, [showMessageIfText]);

  // When the portal container changes, React recreates DOM nodes in the new container. Re-reading
  // the ref is required here — it has to happen after commit so messageRef points to the live node
  // in the new portal.
  useEffect(() => {
    /* eslint-disable @eslint-react/set-state-in-effect */
    if (text && portalContainer) {
      setTargetElement(messageRef.current);
    } else if (targetElementId && portalContainer) {
      setTargetElement(findTargetElement());
    }
    /* eslint-enable @eslint-react/set-state-in-effect */
  }, [findTargetElement, portalContainer, targetElementId, text]);

  // Retry element lookup when the target element renders asynchronously
  // (e.g. after hymnals load in the singing plan tile, or player elements
  // outside the tutorial wrapper).
  useEffect(() => {
    if (text || !targetElementId || targetElement) return;
    const observeTarget = wrapperRef.current ?? document.body;
    const observer = new MutationObserver(() => {
      const el = findTargetElement();
      if (el) {
        setTargetElement(el);
        observer.disconnect();
      }
    });
    observer.observe(observeTarget, { subtree: true, childList: true });
    return () => observer.disconnect();
  }, [findTargetElement, targetElement, targetElementId, text]);

  const messageDialog = (
    <MessageDialog
      style={{
        opacity,
        transition: 'opacity 300ms ease-in',
        bottom: messageBottom,
        boxShadow: suppressSpotlight ? '0 0 20px 5px var(--color-text)' : undefined,
        pointerEvents: shouldFadeMessageDialog ? 'none' : undefined,
      }}
    >
      <MessageWrapper ref={messageRef}>
        <Message onClick={onClick}>{text}</Message>
      </MessageWrapper>
    </MessageDialog>
  );

  return (
    <OuterWrapper ref={wrapperRef}>
      {page}
      {createPortal(messageDialog, portalContainer ?? document.body)}
      {!suppressSpotlight && (
        <Spotlight
          key={portalContainer ? 'scoped' : 'global'}
          targetElement={targetElement}
          onDismiss={onClick}
          options={{ requireClickInSpotlight: !text, padding: padding ?? 0 }}
          isCircular={isCircularSpotlight}
        />
      )}
    </OuterWrapper>
  );
}

const OuterWrapper = styled.div`
  position: relative;
  isolation: isolate;
  width: 100%;
  height: 100%;
`;

const MessageDialog = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  right: 50%;
  transform: translateX(50%);
`;

const MessageWrapper = styled.div`
  padding: 25px;
  border-radius: ${spotlightTextBorderRadius}px;
  background-color: var(--color-background);
`;

const Message = styled.div`
  width: 25ch;
  text-align: center;
`;

function TutorialTodayPage() {
  const today = useCurrentDate();
  return (
    <CurrentOrgMockProvider
      household={{ orgIds: [1] }}
      currentOrgId={1}
      cachedOrgs={[
        {
          id: 1,
          name: 'My Church',
          location: 'Anywhere USA',
          type: OrgType.Church,
          singingPlan: {
            days: [
              {
                date: today,
                songs: [
                  { category: 'This Sunday', slug: firstSongSlug },
                  { category: 'Last Sunday', slug: getSongSlug({ songNumber: '447', hymnalSlug: 'cc2020' }) },
                ],
              },
            ],
          },
        },
      ]}
    >
      <TodayPage />
    </CurrentOrgMockProvider>
  );
}
